import React from 'react';
import PageShell from '../../components/PageShell';
import { SectionHeading, IconBadge, GradientButton, CTABanner, StepChip } from '../../components/common/Primitives';
import LicenceFeeHighlight from '../../components/common/LicenceFeeHighlight';
import { SCHOOL_HOW } from '../../lib/mockSchool';
import { WifiOff, Wifi, PlayCircle } from 'lucide-react';

export default function SchoolHowItWorks() {
  const d = SCHOOL_HOW;
  return (
    <PageShell>
      {/* HERO */}
      <section className="px-6 pt-12 pb-6" data-testid="school-how-hero">
        <div className="mx-auto max-w-[1240px]">
          <div className="grid grid-cols-1 gap-10 lg:grid-cols-[1.15fr_1fr]">
            <div>
              <h1 className="text-[36px] md:text-[48px] font-extrabold leading-[1.05] tracking-tight text-navy">{d.hero.title}</h1>
              <p className="mt-4 max-w-2xl text-[15px] leading-relaxed text-ink-500">{d.hero.desc}</p>
              <div className="mt-6"><LicenceFeeHighlight /></div>
            </div>
            <div className="rounded-3xl border border-canvas-200 bg-gradient-to-br from-purple-50 via-white to-mint-50 p-6 shadow-soft">
              <div className="aspect-video rounded-2xl bg-brand-gradient grain relative overflow-hidden flex items-center justify-center">
                <span className="absolute top-3 left-3 rounded-full bg-white/95 px-2.5 py-1 text-[10px] font-bold tracking-widest text-navy">COMING SOON</span>
                <PlayCircle className="h-14 w-14 text-white/70" />
              </div>
              <div className="mt-4 text-[11.5px] font-bold tracking-widest text-purple">HOW IT WORKS · COMING SOON</div>
              <p className="mt-1 text-[12.5px] text-ink-500">Walkthrough video of the school operating model is being recorded — check back shortly.</p>
            </div>
          </div>
        </div>
      </section>

      {/* SCHOOL MODEL — six steps with icons (no numbers) */}
      <section className="px-6 py-14">
        <div className="mx-auto max-w-[1240px]">
          <SectionHeading eyebrow={d.model.eyebrow} title={d.model.title} desc={d.model.desc} />
          <div className="mt-8 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {d.model.steps.map((s) => (
              <div key={s.n} className="rounded-2xl border border-canvas-200 bg-white p-5 shadow-soft transition-transform hover:-translate-y-0.5 hover:shadow-card" data-testid={`how-step-${s.n}`}>
                <IconBadge icon={s.icon} color={s.color} size="lg" />
                <h3 className="mt-4 text-[15.5px] font-bold text-navy">{s.title}</h3>
                <p className="mt-1.5 text-[13px] leading-relaxed text-ink-500">{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CLASSROOM SCENARIOS */}
      <section className="px-6 py-14 bg-white">
        <div className="mx-auto max-w-[1240px]">
          <SectionHeading eyebrow={d.scenarios.eyebrow} title={d.scenarios.title} desc={d.scenarios.desc} />
          <div className="mt-8 grid grid-cols-1 gap-4 md:grid-cols-3">
            {d.scenarios.items.map((s, i) => (
              <div key={i} className="rounded-2xl border border-canvas-200 bg-white p-6 shadow-soft transition-transform hover:-translate-y-0.5 hover:shadow-card">
                <IconBadge icon={s.icon} color={s.color} size="lg" />
                <h3 className="mt-4 text-[16px] font-bold text-navy">{s.title}</h3>
                <p className="mt-2 text-[13.5px] leading-relaxed text-ink-500">{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ACCOUNTS AND CONNECTIVITY */}
      <section className="px-6 py-14">
        <div className="mx-auto max-w-[1240px]">
          <SectionHeading eyebrow={d.connectivity.eyebrow} title={d.connectivity.title} desc={d.connectivity.desc} />
          <div className="mt-8 grid grid-cols-1 gap-4 md:grid-cols-2">
            {d.connectivity.items.map((c, i) => (
              <div key={i} className="rounded-2xl border border-canvas-200 bg-white p-6 shadow-soft">
                <IconBadge icon={c.icon} color={c.color} size="lg" />
                <h3 className="mt-4 text-[16px] font-bold text-navy">{c.title}</h3>
                <p className="mt-2 text-[13.5px] leading-relaxed text-ink-500">{c.desc}</p>
              </div>
            ))}
          </div>
          <div className="mt-6 flex flex-wrap gap-3">
            <div className="inline-flex items-center gap-2 rounded-full bg-mint-50 px-4 py-2 text-[12.5px] font-semibold text-mint-600 ring-1 ring-mint/20">
              <WifiOff className="h-4 w-4" /> {d.connectivity.footnotes[0].text}
            </div>
            <div className="inline-flex items-center gap-2 rounded-full bg-royal-50 px-4 py-2 text-[12.5px] font-semibold text-royal ring-1 ring-royal/20">
              <Wifi className="h-4 w-4" /> {d.connectivity.footnotes[1].text}
            </div>
          </div>
        </div>
      </section>

      {/* ROLES */}
      <section className="px-6 py-14 bg-white">
        <div className="mx-auto max-w-[1240px]">
          <SectionHeading eyebrow={d.roles.eyebrow} title={d.roles.title} desc={d.roles.desc} />
          <div className="mt-8 grid grid-cols-1 gap-4 md:grid-cols-3">
            {d.roles.items.map((r, i) => (
              <div key={i} className="rounded-2xl border border-canvas-200 bg-white p-6 shadow-soft transition-transform hover:-translate-y-0.5 hover:shadow-card">
                <IconBadge icon={r.icon} color={r.color} size="lg" />
                <h3 className="mt-4 text-[16px] font-bold text-navy">{r.title}</h3>
                <p className="mt-2 text-[13.5px] leading-relaxed text-ink-500">{r.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="px-6 pb-24">
        <div className="mx-auto max-w-[1240px]">
          <CTABanner
            title={d.cta.title}
            desc={d.cta.desc}
            primary={d.cta.primary}
            secondary={d.cta.secondary}
          />
        </div>
      </section>
    </PageShell>
  );
}
